import { Component, ojs, app, payload } from "modular-openscriptjs";
import { dom, tool } from "../../fotastart";
import {
    monetary,
    empty,
    each,
    cleanDispose,
    eParse,
    formatDate,
    either,
} from "../../helpers";
import SolidIcon from "../Blocks/SolidIcon";
import BsIcon from "../Blocks/BsIcon";
import { $e } from "../../ojs.events";

const h = app("h");
const broker = app("broker");

ojs(SolidIcon, BsIcon);

export default class PurchasesView extends Component {
    holder = null;

    render(...args) {
        return h.empty();
    }

    renderView(purchases, ...args) {
        if (empty(purchases)) {
            return h.div(h.span({}, "No purchases for the selected item"));
        }

        return h.div(
            { class: "d-flex flex-column gap-3" },
            each(purchases, (purchase) =>
                h.SinglePurchaseView(purchase, {
                    class: "border p-3 rounded shadow-sm",
                }),
            ),
            ...args,
        );
    }

    $$ui = {
        needs: {
            purchaseView: (ed, event) => this.show(ed, event),
        },
    };

    $$purchase = {
        is: {
            refreshing: (ed, event) => this.closeView(),
        },
        has: {
            refreshError: (ed, event) => this.closeView(),
        },
    };

    async show(ed, event) {
        if (this.holder) {
            cleanDispose(this.holder);
        }

        let data = eParse(ed);

        let options = new tool.ModalOptions();
        options.buttons.right.visible = false;

        let modal = tool.modal(
            "Purchases For This Item",
            this.renderView(data.message.get("purchases")),
            options,
        );

        this.holder = modal.modal.object;
    }

    closeView() {
        if (this.holder) {
            this.holder.hide();
        }
    }
}

class SinglePurchaseView extends Component {
    render(purchase, ...args) {
        const status = purchase?.status?.toLowerCase();
        let statusBadgeClass = "bg-secondary-subtle text-secondary";
        if (["completed", "paid", "success"].includes(status)) {
            statusBadgeClass =
                "bg-success-subtle text-success border border-success-subtle";
        } else if (status === "pending") {
            statusBadgeClass =
                "bg-warning-subtle text-warning-emphasis border border-warning-subtle";
        } else if (["failed", "cancelled"].includes(status)) {
            statusBadgeClass =
                "bg-danger-subtle text-danger border border-danger-subtle";
        }

        return h.div(
            // Card Header: Amount and Status
            h.div(
                {
                    class: "d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom",
                },
                h.div(
                    { class: "d-flex flex-column" },
                    h.span(
                        {
                            class: "text-muted fs-xsm text-uppercase fw-semibold",
                        },
                        "Amount Paid",
                    ),
                    h.span(
                        { class: "fs-5 fw-bold text-dark" },
                        monetary(
                            purchase?.total_paid,
                            purchase?.payment?.currency,
                        ),
                    ),
                ),
                h.span(
                    {
                        class: `badge rounded-pill px-3 py-1.5 fw-semibold text-uppercase fs-xsm ${statusBadgeClass}`,
                    },
                    purchase?.status || "Unknown",
                ),
            ),

            // Details Grid
            h.div(
                { class: "row g-3 mb-3 text-start" },
                h.div(
                    { class: "col-6 d-flex flex-column" },
                    h.span(
                        { class: "text-muted fs-xsm fw-medium" },
                        "Paid Through",
                    ),
                    h.span(
                        { class: "text-dark fw-semibold small" },
                        purchase?.payment?.gateway?.toUpperCase() || "N/A",
                    ),
                ),
                h.div(
                    { class: "col-6 d-flex flex-column" },
                    h.span(
                        { class: "text-muted fs-xsm fw-medium" },
                        "Transaction ID",
                    ),
                    h.span(
                        {
                            class: "text-dark fw-semibold small text-break font-monospace",
                        },
                        purchase?.payment?.transaction_id || "N/A",
                    ),
                ),
                h.div(
                    { class: "col-6 d-flex flex-column" },
                    h.span(
                        { class: "text-muted fs-xsm fw-medium" },
                        "Receiver",
                    ),
                    h.span(
                        { class: "text-dark fw-semibold small" },
                        purchase?.payment?.receiver_name || "N/A",
                    ),
                ),
                h.div(
                    { class: "col-6 d-flex flex-column" },
                    h.span(
                        { class: "text-muted fs-xsm fw-medium" },
                        "Purchased By",
                    ),
                    h.span(
                        { class: "text-dark fw-semibold small" },
                        purchase?.initiator
                            ? `${purchase.initiator.first_name || ""} ${purchase.initiator.last_name || ""}`.trim()
                            : "N/A",
                    ),
                ),
                h.div(
                    { class: "col-12 d-flex flex-column mt-2" },
                    h.span(
                        { class: "text-muted fs-xsm fw-medium" },
                        "Purchase Date",
                    ),
                    h.span(
                        { class: "text-dark small" },
                        formatDate(purchase?.updated_at, "M j, Y g:i A"),
                    ),
                ),
            ),

            // Action Button (if pending)
            either(purchase?.status == "pending", () =>
                h.div(
                    {
                        class: "d-flex justify-content-end mt-2 pt-2 border-top",
                    },
                    h.button(
                        {
                            class: "btn btn-sm btn-outline-primary d-flex gap-2 align-items-center rounded-3 px-3 py-2",
                            onclick: this.method("refreshPurchase", [
                                purchase?.id,
                                "${this}",
                            ]),
                        },
                        h.SolidIcon("arrow-rotate-left"),
                        h.span("Refresh Status"),
                    ),
                ),
            ),
            ...args,
        );
    }

    async refreshPurchase(purchaseId, btn) {
        dom.disable(btn);
        dom.put("Refreshing...", btn);

        broker.send($e.purchase.needs.refresh, payload({ purchaseId }));
    }
}

ojs(PurchasesView, SinglePurchaseView);
